import React, { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import "./dashboard.scss";

function fmt(amount) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount || 0);
}

/**
 * RevenueShareBanner — Alerts the institute when one or more revenue-share
 * service invoices are unpaid (sent / viewed / pending_payment / overdue).
 *
 * Silently no-ops if the institute is on a non-revenue-share billing model
 * or when no unpaid revenue-share invoices exist.
 */
const RevenueShareBanner = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState({ count: 0, total: 0, oldestId: null, hasOverdue: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/service-invoices", {
          params: {
            serviceType: "revenue_share",
            status: "sent,viewed,pending_payment,overdue",
            limit: 50,
          },
        });
        if (cancelled) return;
        const invoices = (data?.invoices || []).filter(
          (i) => i.serviceType === "revenue_share" &&
                 ["sent", "viewed", "pending_payment", "overdue"].includes(i.status)
        );
        const total = invoices.reduce((s, i) => s + (i.totalAmount || 0), 0);
        const hasOverdue = invoices.some((i) => i.status === "overdue");
        const oldest = invoices.sort(
          (a, b) => new Date(a.dueDate || a.createdAt) - new Date(b.dueDate || b.createdAt)
        )[0];
        setInfo({ count: invoices.length, total, oldestId: oldest?._id || null, hasOverdue });
      } catch {
        /* silently ignore — not all institutes have revenue-share */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (info.count === 0) return null;

  const variant = info.hasOverdue ? "danger" : "warning";
  return (
    <div className={`db-grace-banner db-grace-banner--${variant}`}>
      <div className="db-grace-banner__icon">
        <AlertTriangle size={20} />
      </div>
      <div className="db-grace-banner__content">
        <div className="db-grace-banner__title">
          {info.hasOverdue
            ? "Revenue-share invoice overdue"
            : `${info.count} revenue-share invoice${info.count !== 1 ? "s" : ""} pending`}
        </div>
        <div className="db-grace-banner__text">
          You have <strong>{info.count}</strong> unpaid platform commission invoice
          {info.count !== 1 ? "s" : ""} totalling <strong>{fmt(info.total)}</strong>.
          {info.hasOverdue && " Please clear the overdue dues to avoid service interruption."}
        </div>
      </div>
      <button
        className="db-grace-banner__btn"
        onClick={() => navigate(info.oldestId ? `/service-invoices/${info.oldestId}` : "/service-invoices")}
      >
        Pay Now <ArrowRight size={14} />
      </button>
    </div>
  );
};

export default RevenueShareBanner;
